import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import { getSiteAssets, getSiteAssetsCount } from "services/clients/sites/siteAssets";
import { getSiteAssetReferencesForSite, getSiteAssetReferencesCount } from "services/clients/sites/siteAssets/references";
import { defaultPageSize } from "helpers/utils";

const useAssetData = (siteId, customCaptions) => {
	const dispatch = useDispatch();
	const [data, setData] = useState([]);
	const [count, setCount] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	const transformNonSAPData = useCallback((assets) => {
		return assets.map(asset => ({
			id: asset.id,
			assetId: asset.id,
			assetNo: asset.name,
			description: asset.description,
			isAssetOnly: true
		}));
	}, []);

	const transformSAPData = useCallback((referencesData) => {
		return referencesData.map(ref => ({
			id: ref.id,
			assetId: null, // Not provided by /forsite endpoint
			assetNo: ref.assetName,
			referenceId: ref.id,
			referenceName: ref.assetReferenceName,
			description: ref.assetReferenceDescription,
			plannerGroup: ref.plannerGroup,
			workCenter: ref.workCenter,
			isAssetReference: true
		}));
	}, []);


	const fetchAssets = useCallback(async (pageNo = 1, sortField = "", sortOrder = "", searchText = "", showAssetReferences = false) => {
		try {
			setIsLoading(true);
			
			if (showAssetReferences) {
				// Use appropriate SAP mode endpoints
				const [referencesResponse, countResponse] = await Promise.all([
					getSiteAssetReferencesForSite(siteId, pageNo, defaultPageSize(), searchText, sortField, sortOrder),
					getSiteAssetReferencesCount(siteId, searchText)
				]);

				if (!referencesResponse.status) {
					throw new Error(referencesResponse);
				}

				const transformedData = transformSAPData(referencesResponse.data);
				setData(transformedData);
				const totalCount = countResponse.status ? countResponse.data : 0;
				setCount(totalCount);
				
				return { success: true, data: transformedData };
			} else {
				// Use non-SAP mode endpoints with search parameter for count
				const [assetsResponse, countResponse] = await Promise.all([
					getSiteAssets(siteId, pageNo, defaultPageSize(), searchText, sortField, sortOrder),
					getSiteAssetsCount(siteId, searchText)
				]);

				if (!assetsResponse.status) {
					throw new Error(assetsResponse);
				}

				const transformedData = transformNonSAPData(assetsResponse.data);
				setData(transformedData);
				setCount(countResponse.status ? countResponse.data : 0);
				
				return { success: true, data: transformedData };
			}
		} catch (err) {
			dispatch(showError(`Failed to fetch ${customCaptions?.assetPlural || "assets"}.`));
			return { success: false, error: err };
		} finally {
			setIsLoading(false);
		}
	}, [siteId, dispatch, customCaptions, transformNonSAPData, transformSAPData]);

	const searchAssets = useCallback(async (searchText, sortField, sortOrder, showAssetReferences) => {
		try {
			if (showAssetReferences) {
				// Use SAP mode endpoints and refresh count
				const [response, countResponse] = await Promise.all([
					getSiteAssetReferencesForSite(siteId, 1, defaultPageSize(), searchText, sortField, sortOrder),
					getSiteAssetReferencesCount(siteId, searchText)
				]);
				
				if (!response.status) {
					throw new Error(response);
				}

				const transformedData = transformSAPData(response.data);
				setData(transformedData);
				const totalCount = countResponse.status ? countResponse.data : 0;
				setCount(totalCount);
				
				return transformedData;
			} else {
				// Use non-SAP mode endpoints and refresh count
				const [response, countResponse] = await Promise.all([
					getSiteAssets(siteId, 1, defaultPageSize(), searchText, sortField, sortOrder),
					getSiteAssetsCount(siteId, searchText)
				]);
				
				if (!response.status) {
					throw new Error(response);
				}

				const transformedData = transformNonSAPData(response.data);
				setData(transformedData);
				const totalCount = countResponse.status ? countResponse.data : 0;
				setCount(totalCount);
				
				return transformedData;
			}
		} catch (err) {
			dispatch(showError(`Failed to search ${customCaptions?.assetPlural || "assets"}.`));
			return [];
		}
	}, [siteId, dispatch, customCaptions, transformNonSAPData, transformSAPData]);

	const loadMoreAssets = useCallback(async (pageNo, prevData, sortOptions, searchText, showAssetReferences) => {
		try {
			if (showAssetReferences) {
				// Use optimized /forsite endpoint for SAP mode
				const response = await getSiteAssetReferencesForSite(
					siteId,
					pageNo,
					defaultPageSize(),
					searchText,
					sortOptions?.sortField,
					sortOptions?.sortOrder
				);

				if (!response.status) {
					throw new Error(response);
				}

				const newTransformedData = transformSAPData(response.data);
				const combinedData = [...prevData, ...newTransformedData];
				
				setData(combinedData);
				
				return {
					success: true,
					data: combinedData
				};
			} else {
				// Use existing assets endpoint for non-SAP mode
				const response = await getSiteAssets(
					siteId,
					pageNo,
					defaultPageSize(),
					searchText,
					sortOptions?.sortField,
					sortOptions?.sortOrder
				);

				if (!response.status) {
					throw new Error(response);
				}

				const newTransformedData = transformNonSAPData(response.data);
				const combinedData = [...prevData, ...newTransformedData];
				
				setData(combinedData);
				
				return {
					success: true,
					data: combinedData
				};
			}
		} catch (err) {
			dispatch(showError(`Failed to load more ${customCaptions?.assetPlural || "assets"}.`));
			return { success: false, error: err };
		}
	}, [siteId, dispatch, customCaptions, transformNonSAPData, transformSAPData]);

	return {
		data,
		count,
		isLoading,
		fetchAssets,
		searchAssets,
		loadMoreAssets,
		setData
	};
};

export default useAssetData;
